<template>
    <v-card
            class="campaign-card"
    >

        <v-card-text class="pt-6">

            <v-row class="justify-center">
                <v-col cols="12" md="10" class="text-center">
                    <v-row>
                        <v-col class="pb-6">
                            <h2>Campaign Settings</h2>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col class="pb-2">
                            <h4>What is your goal?</h4>
                        </v-col>
                    </v-row>
                    <v-row class="goal-presets">
                        <v-col
                            v-for="preset in presetAmounts"
                            :key="'preset-' + preset"
                            cols="6"
                            sm="3"
                            class="pa-1"
                        >
                            <v-btn
                                :color="selectedPreset === preset ? 'primary' : 'primary'"
                                :outlined="selectedPreset !== preset"
                                depressed
                                block
                                height="44"
                                @click="selectPreset(preset)"
                                :disabled="isCallingApi"
                                class="goal-preset-btn"
                            >
                                ${{ preset.toLocaleString() }}
                            </v-btn>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col class="pt-3">
                            <v-text-field
                                v-model="customAmount"
                                outlined
                                dense
                                label="Or enter custom amount"
                                type="number"
                                min="100"
                                max="10000"
                                prefix="$"
                                :disabled="isCallingApi"
                                :error-messages="amountError"
                                @input="onCustomAmountInput"
                                @focus="clearPresetSelection"
                                @blur="onCustomAmountBlur"
                                hide-details="auto"
                            ></v-text-field>
                            <p class="custom-amount-hint mt-1">Min: $100 · Max: $10,000</p>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col class="pb-0">
                            <h4>Your message</h4>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col class="pt-2">
                            <v-textarea
                                      outlined
                                      dense
                                      v-model="localCampaign.message"
                                      :disabled="isCallingApi"
                                      placeholder="Tell your story and share why you're raising funds..."
                                      rows="3"
                                      counter="500"
                                      maxlength="500"
                            ></v-textarea>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col class="pt-0">
                            <CampaignItemsEditor
                                :is-itemized.sync="isItemized"
                                :items.sync="campaignItems"
                                :goal-amount="localCampaign.amount || 0"
                                :disabled="isCallingApi"
                                @itemsChanged="onItemsChanged"
                            />
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col class="pb-0">
                            <h4>When would you like to receive your raised funds?</h4>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col class="pt-0 pb-0">
                            <v-radio-group column
                                           class="receive-funds-group"
                                           v-model="localCampaign.deposit_type"
                                           :error-messages="depositTypeError"
                                           @input="$v.localCampaign.deposit_type.$touch()"
                                           @blur="$v.localCampaign.deposit_type.$touch()">
                                <v-radio value="delay" class="receive-funds">
                                    <template slot="label">
                                        <div class="receive-funds-label">
                                            <span class="option">2 weeks before my school start date</span>
                                            <span class="extra">Your funds will be deposited on {{ campaignEndDate }}</span>
                                        </div>
                                    </template>
                                </v-radio>
                                <v-radio label="" value="immediate" class="receive-funds">
                                    <template slot="label">
                                        <div class="receive-funds-label">
                                            <span class="option">Immediately after my goal is reached</span>
                                            <span class="extra">Your funds will be deposited immediately after reaching your goal</span>
                                        </div>
                                    </template>
                                </v-radio>
                            </v-radio-group>
                        </v-col>
                    </v-row>
                    <v-row class="d-flex pt-0">
                        <v-col class="pb-0">
                            <v-btn
                                    color="primary"
                                    class="next-btn btn-edurent"
                                    height="45"
                                    width="163"
                                    depressed
                                    :loading="isCallingApi"
                                    @click="update"
                            >
                                Update Settings
                            </v-btn>
                        </v-col>
                    </v-row>
                    <v-row class="d-flex pb-0">
                <v-col class="pb-0">
                    <v-btn
                            color="primary"
                            text
                            depressed
                            class="skip-btn btn-edurent"
                            height="45"
                            width="163"
                            @click="$emit('hideDialog')"
                    >
                        Cancel
                    </v-btn>
                </v-col>
            </v-row>
                </v-col>
            </v-row>

        </v-card-text>

    </v-card>
</template>

<script>
    import { required } from 'vuelidate/lib/validators'
    import UserService from "../services/user.service";
    import moment from "moment";
    import CampaignItemsEditor from "./campaign/CampaignItemsEditor.vue";

    export default {
        name: 'EditCampaign',
        components: {
            CampaignItemsEditor
        },
        props: ['campaign'],
        validations: {
            localCampaign: {
                amount: { required },
                deposit_type: { required },
            }
        },
        data() {
          return {
              presetAmounts: [500, 1000, 1500, 2000],
              selectedPreset: null,
              customAmount: null,
              localCampaign: {
                  amount: null,
                  message: '',
                  deposit_type: ''
              },
              isCallingApi: false,
              isItemized: false,
              campaignItems: []
          }
        },
        computed: {
            amountError() {
                const errors = [];
                if (!this.$v.localCampaign.amount.$dirty) return errors;
                !this.$v.localCampaign.amount.required && errors.push('Amount is required');
                return errors
            },
            depositTypeError() {
                const errors = [];
                if (!this.$v.localCampaign.deposit_type.$dirty) return errors;
                !this.$v.localCampaign.deposit_type.required && errors.push('Deposit is required');
                return errors
            },
            user() {
                return this.$store.getters.getUser;
            },
            campaignEndDate() {
                return moment(this.user.university_start_date).subtract(14, 'd').format('L');
            }
        },
        methods: {
            initLocalCampaign() {
                // Create a local copy of the campaign data
                this.localCampaign = {
                    id: this.campaign.id,
                    amount: this.campaign.amount,
                    message: this.campaign.message || '',
                    deposit_type: this.campaign.deposit_type,
                    user_id: this.campaign.user_id
                }

                // Initialize preset/custom selection based on current amount
                if (this.presetAmounts.includes(this.localCampaign.amount)) {
                    this.selectedPreset = this.localCampaign.amount
                    this.customAmount = null
                } else {
                    this.selectedPreset = null
                    this.customAmount = this.localCampaign.amount
                }

                // Initialize itemized data
                this.isItemized = this.campaign.is_itemized || false
                this.campaignItems = this.campaign.items
                    ? this.campaign.items.map(item => ({
                        id: item.id,
                        category: item.category,
                        name: item.name,
                        goal_amount: item.goal_amount
                    }))
                    : []
            },
            onItemsChanged(validItems, itemsTotal) {
                // Store the items so they get saved with the campaign
                this.campaignItems = validItems

                if (this.isItemized && itemsTotal > 0) {
                    this.localCampaign.amount = itemsTotal
                    this.selectedPreset = null
                    this.customAmount = itemsTotal
                }
            },
            selectPreset(amount) {
                this.selectedPreset = amount
                this.customAmount = null
                this.localCampaign.amount = amount
                this.$v.localCampaign.amount.$touch()
            },
            onCustomAmountInput() {
                if (this.customAmount && parseFloat(this.customAmount) > 0) {
                    this.selectedPreset = null
                    this.localCampaign.amount = parseFloat(this.customAmount)
                    this.$v.localCampaign.amount.$touch()
                }
            },
            clearPresetSelection() {
                this.selectedPreset = null
            },
            onCustomAmountBlur() {
                // If custom amount is empty/invalid, select default preset
                if (!this.customAmount || parseFloat(this.customAmount) <= 0) {
                    this.selectPreset(this.presetAmounts[0])
                }
            },
            update: async function() {
                // Set amount from custom if preset not selected
                if (!this.selectedPreset && this.customAmount) {
                    this.localCampaign.amount = parseFloat(this.customAmount)
                }

                this.$v.localCampaign.$touch();
                if (this.$v.localCampaign.$invalid) {
                    this.$store.commit('setSnackbar', {
                        show: true,
                        message: 'Please fill in all required fields',
                        color: 'red'
                    });
                    return;
                }

                // Validate min/max
                if (this.localCampaign.amount < 100) {
                    this.$store.commit('setSnackbar', {
                        show: true,
                        message: 'Minimum goal amount is $100',
                        color: 'red'
                    });
                    return;
                }

                if (this.localCampaign.amount > 10000) {
                    this.$store.commit('setSnackbar', {
                        show: true,
                        message: 'Maximum goal amount is $10,000',
                        color: 'red'
                    });
                    return;
                }

                this.localCampaign.user_id = this.user.id;

                // Prepare campaign data with itemized info
                const updateData = {
                    campaign: {
                        ...this.localCampaign,
                        is_itemized: this.isItemized
                    }
                };

                // Add items if itemized
                if (this.isItemized && this.campaignItems.length > 0) {
                    updateData.items = this.campaignItems
                        .filter(item => item.name && item.goal_amount > 0)
                        .map(item => ({
                            id: item.id || null,
                            category: item.category,
                            name: item.name,
                            goal_amount: item.goal_amount
                        }));
                } else if (!this.isItemized) {
                    // Clear items if switching to non-itemized
                    updateData.items = [];
                }

                this.isCallingApi = true;
                await UserService.updateCampaign(this.localCampaign.id, updateData);
                this.isCallingApi = false;

                // Refresh campaign data from server after successful save
                this.$emit('getCampaign');
                this.$emit('hideDialog')
            }
        },
        mounted() {
            this.initLocalCampaign()
        }
    }
</script>
<style>

    .campaign-card {
        border-radius: 10px !important;
    }
    .campaign-card h2 {
        font-size: 20px !important;
        font-weight: 700;
        color: #000;
    }
    .campaign-card h4 {
        font-size: 16px;
        font-weight: 400;
        color: #000;
    }

    .goal-presets {
        margin: 0 -4px;
    }

    .goal-preset-btn {
        font-size: 15px !important;
        font-weight: 600;
    }

    .goal-preset-btn.v-btn--outlined {
        border: 2px solid #1976d2 !important;
    }

    .custom-amount-hint {
        font-size: 11px !important;
        color: #666;
        text-align: left;
    }

</style>
